import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useCart, formatINR } from "@/lib/cart";
import {
  Bed, Bath, UtensilsCrossed, Sofa, Building2, Lock,
  ArrowLeft, Loader2, ShieldCheck, IndianRupee,
  Wrench, ZoomIn, X, ShoppingCart, Check, Plus, Zap,
} from "lucide-react";
import SEO from "@/components/SEO";
import { FILE_TYPES } from "@/lib/fileTypes";

export default function PlanDetail() {
  const { slug } = useParams();
  const { t } = useI18n();
  const cart = useCart();
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    api.get(`/plans/${slug}`).then(({ data }) => setPlan(data));
  }, [slug]);

  if (!plan) return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

  const toFullUrl = (src) => src?.startsWith("/api/") ? `${BACKEND_URL}${src}` : src;
  const normalizeImg = (img) => {
    const rawUrl = typeof img === "object" && img !== null ? img.url : img;
    const alt = typeof img === "object" && img !== null ? (img.alt || "") : "";
    return { url: toFullUrl(rawUrl), alt };
  };
  const thumbItem = plan.thumbnail ? { url: toFullUrl(plan.thumbnail), alt: plan.thumbnail_alt || plan.title } : null;
  const allImages = [thumbItem, ...(plan.images || []).map(normalizeImg)].filter(Boolean);
  if (allImages.length === 0) allImages.push({ url: "https://images.pexels.com/photos/4458210/pexels-photo-4458210.jpeg", alt: plan.title });

  const specs = [
    { label: t("planDetail.specWidth"), value: `${plan.width} Ft` },
    { label: t("planDetail.specLength"), value: `${plan.length} Ft` },
    { label: t("planDetail.specPlotArea"), value: `${Math.round(plan.length * plan.width)} Sqft` },
    { label: t("planDetail.specFloors"), value: plan.floors },
    { label: t("planDetail.specBuiltUp"), value: `${plan.area} Sqft` },
    { label: t("planDetail.specFacing"), value: t(`facing.${plan.facing}`) || plan.facing },
    ...(plan.category ? [{ label: t("planDetail.specCategory"), value: plan.category }] : []),
    ...(plan.est_cost_min ? [{ label: t("planDetail.specEstCost"), value: `${plan.est_cost_min}–${plan.est_cost_max} Lakhs` }] : []),
  ];

  const rooms = [
    plan.bedrooms != null && plan.bedrooms > 0 && { icon: Bed, label: t("planDetail.roomBedrooms"), value: plan.bedrooms },
    plan.living_rooms != null && plan.living_rooms > 0 && { icon: Sofa, label: t("planDetail.roomLiving"), value: plan.living_rooms },
    plan.bathrooms != null && plan.bathrooms > 0 && { icon: Bath, label: t("planDetail.roomBathroom"), value: plan.bathrooms },
    plan.kitchens != null && plan.kitchens > 0 && { icon: UtensilsCrossed, label: t("planDetail.roomKitchen"), value: plan.kitchens },
  ].filter(Boolean);

  // File rows: derived from FILE_TYPES + plan.files (which now carries price_paise)
  const fileRows = FILE_TYPES.map(ft => {
    const pf = plan.files.find(f => f.file_type === ft.key);
    const price = pf ? Number(pf.price_paise || 0) : 0;
    return { ...ft, pf, price_paise: price, available: !!pf };
  });

  const buildCartItem = (row) => ({
    plan_id: plan.id,
    plan_title: plan.title,
    plan_slug: plan.slug || plan.id,
    file_type: row.key,
    price_paise: row.price_paise,
  });

  const handleAddToCart = (row) => {
    if (!row.available) return;
    cart.addItem(buildCartItem(row));
  };

  const handleBuyNow = (row) => {
    if (!row.available) return;
    cart.addItem(buildCartItem(row));
    nav("/checkout");
  };

  return (
    <div className="bg-slate-50/50 min-h-screen" data-testid="plan-detail-page">
      <SEO
        title={plan.title}
        description={`${plan.title} — ${plan.length}x${plan.width} Ft plot, ${plan.floors} floor(s), ${plan.area} Sqft built-up, ${plan.facing}-facing. Includes floor plan, BOQ and cost estimate.`}
        path={`/plans/${plan.slug || plan.id}`}
        image={thumbItem?.url}
      />
      <div className="bg-white border-b border-slate-200">
        <div className="nc-container py-3">
          <Link to="/plans" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700"><ArrowLeft className="w-4 h-4" /> {t("planDetail.backToPlans")}</Link>
        </div>
      </div>

      <div className="nc-container py-8 grid lg:grid-cols-12 gap-8">
        {/* ── LEFT COLUMN ── */}
        <div className="lg:col-span-8 space-y-6">

          {/* Image gallery */}
          <div className="flex gap-3">
            {allImages.length > 1 && (
              <div className="flex flex-col gap-2 flex-shrink-0 w-16">
                {allImages.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    className={`w-16 h-14 rounded-lg overflow-hidden border-2 transition ${activeImg === i ? "border-emerald-500" : "border-transparent"}`}>
                    <img src={img.url} alt={img.alt || ""} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div
              className="flex-1 rounded-2xl overflow-hidden bg-white border border-slate-200 flex items-center justify-center relative group cursor-zoom-in"
              onClick={() => setZoomed(true)}
            >
              <img
                src={allImages[activeImg]?.url}
                alt={allImages[activeImg]?.alt || plan.title}
                className="w-full h-auto max-h-[520px] object-contain"
              />
              <div className="absolute bottom-3 right-3 bg-black/40 text-white rounded-lg px-2 py-1 text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition pointer-events-none">
                <ZoomIn className="w-3.5 h-3.5" /> {t("planDetail.clickToZoom")}
              </div>
            </div>
          </div>

          {zoomed && (
            <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setZoomed(false)}>
              <button className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition" onClick={() => setZoomed(false)}>
                <X className="w-6 h-6" />
              </button>
              <img src={allImages[activeImg]?.url} alt={allImages[activeImg]?.alt || plan.title}
                className="max-w-full max-h-full object-contain rounded-lg" onClick={e => e.stopPropagation()} />
            </div>
          )}

          {/* Product Description */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2.5">
              <div className="w-1 h-5 rounded-full bg-emerald-500 flex-shrink-0" />
              <h2 className="font-semibold text-slate-800 text-sm uppercase tracking-wider">{t("planDetail.productDescription")}</h2>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {specs.map(s => (
                <div key={s.label} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-emerald-700" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">{s.label}</div>
                    <div className="font-semibold text-slate-900 text-sm">{s.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Floor Description (room counts) */}
          {rooms.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2.5">
                <div className="w-1 h-5 rounded-full bg-emerald-500 flex-shrink-0" />
                <h2 className="font-semibold text-slate-800 text-sm uppercase tracking-wider">{t("planDetail.floorDescription")}</h2>
              </div>
              <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {rooms.map(r => (
                  <div key={r.label} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
                    <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <r.icon className="w-4 h-4 text-emerald-700" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">{r.label}</div>
                      <div className="font-bold text-slate-900">{r.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Plot Details (description + tags) */}
          {plan.description && (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2.5">
                <div className="w-1 h-5 rounded-full bg-emerald-500 flex-shrink-0" />
                <h2 className="font-semibold text-slate-800 text-sm uppercase tracking-wider">{t("planDetail.plotDetails")}</h2>
              </div>
              <div className="p-5">
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{plan.description}</p>
              </div>
            </div>
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            {plan.category && <span className="nc-chip">{plan.category}</span>}
            {plan.state && <span className="nc-chip">{plan.state}</span>}
            <span className="nc-chip"><ShieldCheck className="w-3 h-3" /> {t("planDetail.vastuCompliant")}</span>
          </div>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div className="lg:col-span-4">
          <div className="sticky top-20 space-y-4">

            {/* Title + cost card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-semibold text-slate-900 leading-snug" style={{ fontFamily: "Poppins" }}>{plan.title}</h1>
                {plan.naksha_no && (
                  <span className="text-xs font-mono px-2 py-1 rounded bg-slate-100 text-slate-600">
                    {plan.naksha_no}
                  </span>
                )}
              </div>
              {plan.title_hi && <p className="text-sm text-slate-500 mt-0.5">{plan.title_hi}</p>}

              {plan.est_cost_min && (
                <div className="mt-4 border-t border-slate-100 pt-4">
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">{t("planDetail.estCost")}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <IndianRupee className="w-5 h-5 text-slate-900" />
                    <span className="text-2xl font-bold text-slate-900">{plan.est_cost_min} – {plan.est_cost_max} L</span>
                  </div>
                </div>
              )}

              <Link to={`/customize?plan_id=${plan.id}`} className="mt-4 nc-btn-primary w-full justify-center flex">
                <Wrench className="w-4 h-4" /> {t("planDetail.customize")}
              </Link>
            </div>

            {/* Download Bundle card with prices */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 nc-soft-shadow">
              <h2 className="font-semibold text-slate-900">{t("planDetail.downloadBundle")}</h2>
              <p className="text-xs text-slate-500 mt-0.5">{t("planDetail.bundleNote")}</p>

              <div className="mt-4 space-y-2">
                {fileRows.map(row => {
                  const inCart = cart.has(plan.id, row.key);
                  return (
                    <div key={row.key}
                      className={`flex items-center justify-between p-3 rounded-xl border transition ${row.available ? "border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/30" : "border-slate-200 bg-slate-50/60 opacity-70"}`}
                      data-testid={`file-row-${row.key}`}>
                      <span className="flex items-center gap-2.5 min-w-0">
                        <span className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                          <row.icon className="w-4 h-4" />
                        </span>
                        <span className="text-left min-w-0">
                          <span className="block font-medium text-slate-900 text-sm">{t(row.labelKey)}</span>
                          <span className="block text-xs text-slate-500">{t("planDetail.pdfWatermarked")}</span>
                        </span>
                      </span>
                      <span className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-sm font-bold text-slate-900">{formatINR(row.price_paise)}</span>
                        {!row.available ? (
                          <Lock className="w-4 h-4 text-slate-400" />
                        ) : inCart ? (
                          <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {t("planDetail.inCart")}</span>
                        ) : (
                          <button onClick={() => handleAddToCart(row)}
                            className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 px-2 py-1 rounded inline-flex items-center gap-1"
                            data-testid={`add-cart-${row.key}`}>
                            <Plus className="w-3.5 h-3.5" /> {t("planDetail.add")}
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Action buttons — Buy Now defaults to floor_plan; Add to Cart adds all paid items */}
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    fileRows.filter(r => r.available).forEach(r => cart.addItem(buildCartItem(r)));
                  }}
                  className="nc-btn-secondary justify-center text-sm py-2.5"
                  data-testid="add-all-to-cart-btn">
                  <ShoppingCart className="w-4 h-4" /> {t("planDetail.addToCart")}
                </button>
                <button
                  onClick={() => {
                    const fp = fileRows.find(r => r.key === "floor_plan" && r.available);
                    if (fp) handleBuyNow(fp);
                  }}
                  className="nc-btn-primary justify-center text-sm py-2.5"
                  data-testid="buy-now-btn">
                  <Zap className="w-4 h-4" /> {t("planDetail.buyNow")}
                </button>
              </div>

              {cart.count > 0 && (
                <Link to="/cart" className="mt-3 block text-center text-xs font-semibold text-emerald-700 hover:underline" data-testid="view-cart-link">
                  {t("planDetail.viewCart", { n: cart.count })}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
